import { FusionInputField } from 'components';
import BaseSelect from 'components/Selects/BaseSelect/BaseSelect';
import { findSelectOption } from 'components/Selects/BaseSelect/utils/baseSelect.utils';
import BaseModal from 'components/Modals/BaseModal';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import { usePerms } from 'contexts/PermsContext';
import layout from 'data/SystemTypeForm.json';
import {
  selectDeviceTypeMap,
  selectSystemTypeMap,
} from 'features/Integrations/state/selectors/integrationSelectors';
import { useEffect, useMemo, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import {
  useFusionGetIntegrationsQuery,
  useGetLicenseInfoQuery,
} from 'services/integrations/integrations.api';
import { getSystemLicenseClass } from '../../../config/licensepools.config';
import {
  getAdvancedSystemLicenseLimit,
  getAdvancedSystemUsageCount,
  getDeviceTypesBySystemType,
  getLicenseSystems,
  getSystemTypeUsageCounts,
} from '../../../utils/license.utils';

export default function SystemModal({ onSubmit, onCancel, system: systemToEdit, loading }) {
  const systemTypeMap = useSelector(selectSystemTypeMap);
  const deviceTypeMap = useSelector(selectDeviceTypeMap);
  const { data: licenseInfo = {} } = useGetLicenseInfoQuery();
  const { data: integrations = [] } = useFusionGetIntegrationsQuery();
  const { data: connectedDevices = [] } = useGetConnectedDevicesQuery();

  const perms = usePerms();
  const canEditCredentials = perms.testPermission(
    [8, 9, 10, 11, 12, 13],
    ['SystemEditCredentials'],
  );
  const createOrEditMode = systemToEdit ? 'edit' : 'create'; // Determine mode based on presence of systemToEdit

  // Normalize system type names directly from the system type map payload.
  // Do not pre-filter by existing integrations here, which can produce an empty
  // dropdown despite valid system types being available.
  const availableSystemTypes = useMemo(() => {
    const entries = Array.isArray(systemTypeMap)
      ? systemTypeMap
      : Object.values(systemTypeMap || {});

    const names = entries.map((system) => system?.system_type || '').filter(Boolean);
    return [...new Set(names)];
  }, [systemTypeMap]);

  const [formData, setFormData] = useState(() => {
    if (!systemToEdit) return {};
    return {
      ...systemToEdit,
      system_name: systemToEdit.system_name,
      password: '',
    };
  });

  // system_type is the type name itself, so no index round-trip is needed.
  const selectedSystemType = formData?.system_type || '';
  const systemTypeOptions = useMemo(
    () => (availableSystemTypes || []).map((name) => ({ value: name, label: name })),
    [availableSystemTypes],
  );
  // Same input the Connected Systems table classifies from, so "licenses remaining" here
  // agrees with the N/limit indexes rendered behind this modal.
  const licenseSystems = useMemo(
    () => getLicenseSystems(integrations, connectedDevices),
    [integrations, connectedDevices],
  );
  // License class is resolved per system type, so the same lookup answers for a system being
  // created and one being edited. See getLicenseSystems for why attribution is type-wide.
  const deviceTypesBySystemType = useMemo(
    () => getDeviceTypesBySystemType(connectedDevices),
    [connectedDevices],
  );
  const selectedSystemDeviceTypes = deviceTypesBySystemType.get(selectedSystemType) ?? [];
  const selectedSystemRequiresLicense =
    getSystemLicenseClass(selectedSystemType, selectedSystemDeviceTypes) === 'advanced';
  const systemTypeUsageCounts = useMemo(
    () => getSystemTypeUsageCounts(licenseSystems),
    [licenseSystems],
  );
  const selectedSystemUsed = getAdvancedSystemUsageCount(systemTypeUsageCounts);
  const selectedSystemLimit = selectedSystemRequiresLicense
    ? getAdvancedSystemLicenseLimit(licenseInfo)
    : 0;
  const selectedSystemRemaining = selectedSystemRequiresLicense
    ? Math.max(selectedSystemLimit - selectedSystemUsed, 0)
    : 0;

  // If systemTypes arrives async, keep edit form stable (optional safety)
  useEffect(() => {
    if (!systemToEdit) return;
    setFormData((prev) => ({
      ...prev,
      ...systemToEdit,
      system_name: systemToEdit.system_name,
      email: systemToEdit.email ?? (canEditCredentials ? 'webops@rescalert.com' : ''),
      password: '',
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableSystemTypes]);

  const renderGatedInput = ({ label, dataField, type }) => {
    const isCredentialField = dataField === 'email' || dataField === 'password';
    const disabled = isCredentialField && !canEditCredentials;
    const placeholderText = disabled ? '- RESTRICTED BY ADMIN -' : '';

    return (
      <FusionInputField
        label={label}
        type={type}
        value={formData[dataField] || ''}
        onChange={(value) => setFormData({ ...formData, [dataField]: value })}
        autoComplete={type === 'password' ? 'off' : undefined}
        disabled={disabled}
        placeholder={placeholderText}
      />
    );
  };

  const renderField = (field) => {
    switch (field.type) {
      case 'input-field': {
        const dataField = field['data-field'];
        return renderGatedInput({ label: field.label, dataField });
      }
      case 'password-field': {
        // Always label the field as "Password" (not "New Password").
        const passwordLabel = field.label || 'Password';
        return renderGatedInput({
          label: passwordLabel,
          dataField: field['data-field'],
          type: 'password',
        });
      }
      default:
        return null;
    }
  };

  // Basic system types report 0 remaining because they draw from no pool at all, which must
  // not be read as "the pool is exhausted".
  const isAtSystemLimit =
    createOrEditMode === 'create' &&
    !!formData?.system_type &&
    selectedSystemRequiresLicense &&
    selectedSystemRemaining <= 0;

  const canSubmit = () => {
    const layoutItem = layout[selectedSystemType?.toLowerCase()] ?? layout['default']; //default found int SystemTypeForm.json

    // Collect required fields from per-field flags if present
    const flaggedRequired = (layoutItem?.components || [])
      .map((component) => ({ fieldName: component['data-field'], fieldComponent: component })) // FieldName: string, fieldComponent: object
      .filter(
        (entry) =>
          entry.fieldName &&
          (createOrEditMode === 'create'
            ? !!entry.fieldComponent.requiredOnCreate
            : !!entry.fieldComponent.requiredOnEdit),
      )
      .map((entry) => entry.fieldName);
    //Generate final required array with system_type and system_name as base required fields
    const required = ['system_type', 'system_name', ...(flaggedRequired || [])];

    return !required
      .map((key) => {
        if (key === 'system_type') return !!formData[key];
        // If the field is a credential field and the user lacks credential perms,
        // disallow submission because they cannot provide required values.
        //TODO: Display a warning message or tooltip to explain why the field is disabled and submission is blocked.
        if ((key === 'email' || key === 'password') && !canEditCredentials) return false;
        return !!formData[key];
      })
      .includes(false);
  };

  const submit = () => {
    const deviceTypesList = Array.isArray(deviceTypeMap)
      ? deviceTypeMap
      : Object.values(deviceTypeMap || {});
    const rest = { ...formData };

    const payload = {
      ...rest,
      system_type: selectedSystemType,
      device_type: deviceTypesList
        .filter((device) => device.system_type === selectedSystemType)
        .map((device) => device.id),
    };
    onSubmit(payload);
  };

  return (
    <BaseModal
      show={true}
      handleShow={onCancel}
      title={systemToEdit ? 'Edit System' : 'New System'}
    >
      <Form autoComplete="off">
        <div className="d-flex flex-column gap-3">
          <BaseSelect
            label="System Type"
            isSearchable
            // Clearing is how the operator gets back to the neutral "Select" state after a
            // wrong pick, which the old dropdown's own "Select" row used to provide. Clearing
            // maps to an empty system_type, exactly what that row's `-1` resolved to, so the
            // layout fields and the license block collapse the way they did before.
            isClearable
            selectProps={{
              options: systemTypeOptions,
              value: findSelectOption(systemTypeOptions, selectedSystemType),
              placeholder: 'Select',
              isDisabled: !!systemToEdit,
              onChange: (option) => {
                setFormData({
                  ...formData,
                  system_type: option?.value ?? '',
                  email: canEditCredentials ? (formData?.email ?? 'webops@rescalert.com') : '',
                  password: null,
                });
              },
            }}
          />

          <FusionInputField
            label="System Name"
            value={formData?.system_name || ''}
            onChange={(value) => setFormData({ ...formData, system_name: value })}
          />

          {!!selectedSystemType &&
            (() => {
              const layoutItem = layout[selectedSystemType.toLowerCase()] ?? layout['default'];

              return (
                layoutItem?.components
                  ?.filter((field) => field['data-field'] !== 'confirmPassword')
                  .map((field, index) => (
                    <div key={`${field['data-field']}-${index}`}>{renderField(field)}</div>
                  )) || null
              );
            })()}

          {selectedSystemType && (
            <div className="fw-bold fs-6">
              <p className="mb-0">Available Licenses:</p>
              <div>
                {selectedSystemRequiresLicense
                  ? `${selectedSystemRemaining} of ${selectedSystemLimit} (Licenses remaining)`
                  : 'No license required'}
              </div>
            </div>
          )}
        </div>

        {onSubmit && (
          <div className="d-flex justify-content-sm-end justify-content-center">
            <Button
              disabled={!canSubmit() || isAtSystemLimit}
              className="border-0 mt-3 py-2 px-4 rounded-4 w-100 w-sm-auto"
              onClick={submit}
              variant="primary"
            >
              {systemToEdit ? 'Submit' : 'Connect'}
            </Button>
          </div>
        )}
      </Form>

      {loading && (
        <div>
          <WidgetLoadingAnimation disableText />
        </div>
      )}
    </BaseModal>
  );
}
